import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import Confetti from "react-confetti";
import { Bell, CalendarDays, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { NotificationService } from "@/services/APIs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PTS_IN_APP_NOTIFICATION_APP } from "../../utils/ptsInAppNotifications";

type NotificationCategory =
  | "Event"
  | "Donation"
  | "Announcement"
  | "Greeting"
  | "Volunteering"
  | "Chat"
  | "Membership"
  | "Booking";

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  timeAgo: string;
  category: NotificationCategory;
  isRead: boolean;
  /** Raw `extra.type` from API (e.g. `holiday_reminder`, `birthday`). */
  extraType: string;
};

const PAGE_SIZE = 20;

const getCategoryFromType = (
  rawType?: string,
  title?: string,
  extra?: Record<string, unknown> | null
): NotificationCategory => {
  const type = (rawType ?? "").toLowerCase();
  const titleLower = (title ?? "").toLowerCase();
  if (
    titleLower.includes("check-in") ||
    titleLower.includes("checkin") ||
    extra?.checkin_id
  ) {
    return "Announcement";
  }
  if (type.includes("holiday") || type.includes("event")) {
    return "Event";
  }
  if (type.includes("donation")) {
    return "Donation";
  }
  if (type.includes("announcement")) {
    return "Announcement";
  }
  if (type.includes("greeting") || type.includes("birthday")) {
    return "Greeting";
  }
  if (type.includes("volunteer")) {
    return "Volunteering";
  }
  if (type.includes("chat") || type.includes("message")) {
    return "Chat";
  }
  if (type.includes("member")) {
    return "Membership";
  }
  if (type.includes("booking")) {
    return "Booking";
  }
  return "Announcement";
};

const getRelativeTime = (dateValue?: string) => {
  if (!dateValue) {
    return "Just now";
  }
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return "Just now";
  }
  const diffMs = Date.now() - parsed.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) {
    return "Just now";
  }
  if (diffMs < hour) {
    return `${Math.floor(diffMs / minute)} min ago`;
  }
  if (diffMs < day) {
    return `${Math.floor(diffMs / hour)} hours ago`;
  }
  return `${Math.floor(diffMs / day)} days ago`;
};

/** Highlights negative duration values like -00:30 or -1:30 in red (e.g. check-in / hours shortfall). */
function renderMessageWithNegativeTimeHighlight(text: string): ReactNode {
  if (!text) {
    return null;
  }
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  const re = /-\d{1,2}:\d{2}(?::\d{2})?/g;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    parts.push(
      <span key={`t-${i++}`} className="font-bold text-red-600">
        {match[0]}
      </span>
    );
    last = re.lastIndex;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts.length > 0 ? parts : text;
}

/** Confetti only for unread items whose title matches these phrases (case-insensitive). */
const CONFETTI_TITLE_SNIPPETS = [
  "happy birthday",
  "upcoming holiday reminder",
] as const;

function shouldTriggerConfettiForNotification(item: NotificationItem): boolean {
  if (item.isRead) {
    return false;
  }
  const titleNorm = item.title.trim().toLowerCase();
  return CONFETTI_TITLE_SNIPPETS.some(snippet => titleNorm.includes(snippet));
}

const CONFETTI_COLORS = [
  "#0c3055",
  "#ff9d00",
  "#f472b6",
  "#34d399",
  "#fbbf24",
  "#60a5fa",
  "#c084fc",
];

type ConfettiBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/**
 * Confetti should cover only the PTS main scroll area (right of sidebar), not the left nav.
 * Tracks the inner `main.overflow-auto` from PTSLayout via ResizeObserver + resize.
 */
function useMainContentConfettiBounds(
  anchorRef: RefObject<HTMLElement | null>,
  /** Re-run measure when confetti starts (sidebar width may still be animating). */
  remeasureTrigger: boolean
): ConfettiBounds {
  const [bounds, setBounds] = useState<ConfettiBounds>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    const update = () => {
      const el = anchorRef.current;
      const scrollMain = el?.closest("main.overflow-auto");
      if (!scrollMain) {
        setBounds({
          left: 0,
          top: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        });
        return;
      }
      const r = scrollMain.getBoundingClientRect();
      setBounds({
        left: r.left,
        top: r.top,
        width: r.width,
        height: r.height,
      });
    };

    update();
    const scrollMain = anchorRef.current?.closest("main.overflow-auto");
    const ro = scrollMain ? new ResizeObserver(update) : null;
    if (scrollMain && ro) {
      ro.observe(scrollMain);
    }
    window.addEventListener("resize", update);
    const t1 = window.setTimeout(update, 100);
    const t2 = window.setTimeout(update, 280);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [anchorRef, remeasureTrigger]);

  return bounds;
}

const NotificationsPage = () => {
  const [page, setPage] = useState(1);
  const [showConfetti, setShowConfetti] = useState(false);
  const queryClient = useQueryClient();
  const celebrationSignatureRef = useRef<string | null>(null);
  const confettiAnchorRef = useRef<HTMLDivElement>(null);
  const confettiBounds = useMainContentConfettiBounds(
    confettiAnchorRef,
    showConfetti
  );

  const { data, isLoading } = useQuery({
    queryKey: ["in-app-notifications", page],
    queryFn: async () => {
      const res = await NotificationService.getInAppNotifications({
        page,
        page_size: PAGE_SIZE,
        app: PTS_IN_APP_NOTIFICATION_APP,
      });
      if (!res || !Array.isArray(res.results)) {
        throw new Error("Invalid notifications response");
      }
      return res;
    },
    refetchInterval: 30000,
  });

  const markOneReadMutation = useMutation({
    mutationFn: (id: string) =>
      NotificationService.markInAppNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["in-app-notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["in-app-unread-count", PTS_IN_APP_NOTIFICATION_APP],
      });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => NotificationService.markAllInAppNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["in-app-notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["in-app-unread-count", PTS_IN_APP_NOTIFICATION_APP],
      });
    },
  });

  const notifications = useMemo<NotificationItem[]>(() => {
    const results = Array.isArray(data?.results) ? data.results : [];
    return results.map(item => ({
      id: item.id,
      title: item.title,
      message: item.message,
      timeAgo: getRelativeTime(item.created_on),
      category: getCategoryFromType(
        String(item?.extra?.type ?? ""),
        item.title,
        item.extra
      ),
      isRead: item.is_read,
      extraType: String(item?.extra?.type ?? ""),
    }));
  }, [data?.results]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const celebratory = notifications.filter(
      shouldTriggerConfettiForNotification
    );
    if (celebratory.length === 0) {
      return;
    }
    const signature = celebratory
      .map(n => n.id)
      .sort()
      .join("|");
    if (celebrationSignatureRef.current === signature) {
      return;
    }
    celebrationSignatureRef.current = signature;
    setShowConfetti(true);
  }, [isLoading, notifications]);

  const unreadCount = useMemo(
    () => notifications.filter(item => !item.isRead).length,
    [notifications]
  );

  const markAllRead = () => {
    markAllReadMutation.mutate();
  };

  const handleNotificationClick = (item: NotificationItem) => {
    if (item.isRead || markOneReadMutation.isPending) {
      return;
    }
    markOneReadMutation.mutate(item.id);
  };

  return (
    <div ref={confettiAnchorRef} className="relative min-h-0">
      {showConfetti &&
        confettiBounds.width > 0 &&
        confettiBounds.height > 0 && (
          <div
            className="pointer-events-none fixed z-[100]"
            style={{
              left: confettiBounds.left,
              top: confettiBounds.top,
              width: confettiBounds.width,
              height: confettiBounds.height,
            }}
            aria-hidden
          >
            <Confetti
              width={confettiBounds.width}
              height={confettiBounds.height}
              recycle={false}
              numberOfPieces={320}
              gravity={0.32}
              tweenDuration={5000}
              colors={CONFETTI_COLORS}
              onConfettiComplete={() => setShowConfetti(false)}
            />
          </div>
        )}
      <div className="mx-auto w-full max-w-5xl">
        <div className="bg-card rounded-2xl border p-5 shadow-sm md:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-foreground text-2xl font-semibold">
                Notifications
              </h1>
              <p className="mt-1 text-sm">
                {unreadCount > 0 ? (
                  <>
                    <span className="text-muted-foreground">You have </span>
                    <span className="text-primary font-semibold">
                      {unreadCount}
                    </span>
                    <span className="text-muted-foreground">
                      {" "}
                      unread notification{unreadCount === 1 ? "" : "s"}
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    You&apos;re all caught up
                  </span>
                )}
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={markAllRead}
              disabled={markAllReadMutation.isPending || unreadCount === 0}
              className="text-primary hover:bg-secondary/20 rounded-xl"
            >
              <Check className="mr-2 h-4 w-4" />
              Mark All Read
            </Button>
          </div>

          {isLoading && (
            <div className="text-muted-foreground py-8 text-center text-sm">
              Loading notifications...
            </div>
          )}

          {!isLoading && notifications.length === 0 && (
            <div className="text-muted-foreground py-8 text-center text-sm">
              No notifications found.
            </div>
          )}

          <div className="space-y-3">
            {notifications.map(item => {
              const isUnread = !item.isRead;

              if (!isUnread) {
                return (
                  <div
                    key={item.id}
                    className="border-border bg-muted/40 relative flex items-start gap-3 rounded-xl border p-4 opacity-70 transition-colors"
                  >
                    <div className="bg-secondary/30 text-primary mt-0.5 rounded-lg p-2">
                      {item.category === "Event" ? (
                        <CalendarDays className="h-4 w-4" />
                      ) : (
                        <Bell className="h-4 w-4" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-foreground text-sm font-medium">
                        {item.title}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {renderMessageWithNegativeTimeHighlight(item.message)}
                      </p>
                      <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                        <span>{item.timeAgo}</span>
                        <span>|</span>
                        <span>{item.category}</span>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotificationClick(item)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleNotificationClick(item);
                    }
                  }}
                  className="border-primary/20 from-secondary/5 to-secondary/12 ring-primary/8 focus-visible:ring-primary border-l-primary hover:border-primary/50 hover:border-l-primary from-primary/12 to-secondary/15 hover:shadow-primary/10 hover:ring-primary/25 group relative flex cursor-pointer items-start gap-3 rounded-xl border border-l-4 bg-gradient-to-r p-4 shadow-sm ring-1 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:ring-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:translate-y-0 active:scale-[0.997]"
                >
                  <div className="bg-primary/15 text-primary group-hover:bg-primary/25 group-hover:ring-primary/20 mt-0.5 shrink-0 rounded-lg p-2 shadow-inner transition-all duration-300 ease-out group-hover:shadow-md group-hover:ring-1">
                    {item.category === "Event" ? (
                      <CalendarDays className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                    ) : (
                      <Bell className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-foreground group-hover:text-primary text-base font-bold tracking-[0.4px] transition-colors duration-300">
                        {item.title}
                      </p>
                    </div>
                    <p className="text-foreground/90 group-hover:text-foreground mt-1 text-sm leading-relaxed transition-colors duration-300">
                      {renderMessageWithNegativeTimeHighlight(item.message)}
                    </p>
                    <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span>{item.timeAgo}</span>
                      <span className="text-border">|</span>
                      <span className="bg-muted text-primary group-hover:bg-secondary/50 group-hover:text-primary rounded-md px-1.5 py-0.5 text-[11px] transition-colors duration-300">
                        {item.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
                    <span
                      className="bg-primary ring-primary/35 ring-offset-background group-hover:ring-primary/30 h-2.5 w-2.5 rounded-full ring-2 ring-offset-2 transition-all duration-300 group-hover:scale-125 group-hover:ring-4"
                      aria-hidden
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page <= 1 || isLoading}
            >
              Previous
            </Button>
            <span className="text-muted-foreground text-xs">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(prev => prev + 1)}
              disabled={
                isLoading ||
                page * PAGE_SIZE >= (data?.count ?? notifications.length)
              }
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
